import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddItemPayload,
  AddServicoPayload,
  BASE_COMERCIAL_KEYS,
  CategoriaAnexoBaseComercial,
  CreateBaseComercialPayload,
  DominioBaseComercial,
  ListBasesComerciaisParams,
  UpdateBaseComercialPayload,
  UpdateItemPayload,
  adicionarItem,
  adicionarServico,
  aprovarBaseComercial,
  atualizarBaseComercial,
  atualizarItem,
  buscarBaseComercial,
  cancelarBaseComercial,
  confirmarRevisaoCondicoes,
  criarBaseComercial,
  enviarAnexo,
  gerarNovaVersao,
  listarAnexos,
  listarAprovacoes,
  listarBasesComerciais,
  listarDominio,
  listarHistorico,
  listarItens,
  listarPendentesAprovacao,
  listarServicos,
  listarVersoes,
  publicarBaseComercial,
  reativarBaseComercial,
  rejeitarBaseComercial,
  removerAnexo,
  removerBaseComercial,
  removerItem,
  removerServico,
  solicitarAprovacao,
  substituirBaseComercial,
  suspenderBaseComercial,
} from '../api';

/** React Query hooks da Base Comercial (US-COM-005). */

export { BASE_COMERCIAL_KEYS };

// -------- Leitura ----------------------------------------------------

export function useBasesComerciais(params?: ListBasesComerciaisParams) {
  return useQuery({
    queryKey: BASE_COMERCIAL_KEYS.list(params),
    queryFn: () => listarBasesComerciais(params),
  });
}

export function useBaseComercial(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.detail(id) : ['base-comercial', 'detail', 'noop'],
    queryFn: () => buscarBaseComercial(id as string),
    enabled: !!id,
  });
}

export function useServicosDaBase(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.services(id) : ['base-comercial', 'services', 'noop'],
    queryFn: () => listarServicos(id as string),
    enabled: !!id,
  });
}

export function useItensDaBase(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.items(id) : ['base-comercial', 'items', 'noop'],
    queryFn: () => listarItens(id as string),
    enabled: !!id,
  });
}

export function useVersoesDaBase(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.versions(id) : ['base-comercial', 'versions', 'noop'],
    queryFn: () => listarVersoes(id as string),
    enabled: !!id,
  });
}

export function useHistoricoDaBase(id: string | undefined, params?: { limit?: number }) {
  return useQuery({
    queryKey: id
      ? [...BASE_COMERCIAL_KEYS.history(id), params]
      : ['base-comercial', 'history', 'noop'],
    queryFn: () => listarHistorico(id as string, params),
    enabled: !!id,
  });
}

export function useAprovacoesDaBase(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.approvals(id) : ['base-comercial', 'approvals', 'noop'],
    queryFn: () => listarAprovacoes(id as string),
    enabled: !!id,
  });
}

export function usePendentesAprovacao(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...BASE_COMERCIAL_KEYS.pendingApproval(), params?.limit ?? null, params?.offset ?? null],
    queryFn: () => listarPendentesAprovacao(params),
  });
}

export function useAnexosDaBase(id: string | undefined) {
  return useQuery({
    queryKey: id ? BASE_COMERCIAL_KEYS.attachments(id) : ['base-comercial', 'attachments', 'noop'],
    queryFn: () => listarAnexos(id as string),
    enabled: !!id,
  });
}

/**
 * Domínios administráveis (serviços, perfis, precificação, índices).
 * Mudam raramente — cache de 5 min reduz round-trips do formulário,
 * mesmo racional de `useEtapas` em Oportunidades.
 */
export function useDominio(tipo: DominioBaseComercial, includeInactive = false) {
  return useQuery({
    queryKey: [...BASE_COMERCIAL_KEYS.dominio(tipo), includeInactive],
    queryFn: () => listarDominio(tipo, includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

// -------- Mutations --------------------------------------------------

/**
 * Invalida tudo que depende de uma Base específica.
 *
 * Invalidamos também o grupo de versões: uma edição sobre versão
 * publicada gera uma versão nova (RN-COM-005), então a lista de versões
 * e a listagem principal mudam mesmo que o `id` consultado não tenha
 * mudado.
 */
function useInvalidarBase() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: BASE_COMERCIAL_KEYS.all });
    if (id) {
      void qc.invalidateQueries({ queryKey: BASE_COMERCIAL_KEYS.detail(id) });
    }
  };
}

export function useCriarBaseComercial() {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (payload: CreateBaseComercialPayload) => criarBaseComercial(payload),
    onSuccess: (base) => invalidar(base.id),
  });
}

/**
 * **A resposta pode ter `id` diferente do enviado**: sobre versão
 * publicada/vigente o backend gera nova versão e devolve ela
 * (RN-COM-005). Quem chama deve navegar para `data.id`.
 */
export function useAtualizarBaseComercial() {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBaseComercialPayload }) =>
      atualizarBaseComercial(id, payload),
    onSuccess: (base) => invalidar(base.id),
  });
}

export function useRemoverBaseComercial() {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (id: string) => removerBaseComercial(id),
    onSuccess: () => invalidar(),
  });
}

// -------- Estrutura --------------------------------------------------

export function useAdicionarServico(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (payload: AddServicoPayload) => adicionarServico(baseId, payload),
    onSuccess: () => invalidar(baseId),
  });
}

export function useRemoverServico(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (servicoId: string) => removerServico(baseId, servicoId),
    onSuccess: () => invalidar(baseId),
  });
}

export function useAdicionarItem(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (payload: AddItemPayload) => adicionarItem(baseId, payload),
    // Os totais da Base são recalculados no backend a cada escrita de
    // item — o detalhe precisa ser reconsultado, não só a lista de itens.
    onSuccess: () => invalidar(baseId),
  });
}

export function useAtualizarItem(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: UpdateItemPayload }) =>
      atualizarItem(baseId, itemId, payload),
    onSuccess: () => invalidar(baseId),
  });
}

export function useRemoverItem(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (itemId: string) => removerItem(baseId, itemId),
    onSuccess: () => invalidar(baseId),
  });
}

// -------- Ciclo de vida ----------------------------------------------

export function useSolicitarAprovacao(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: () => solicitarAprovacao(baseId),
    onSuccess: () => invalidar(baseId),
  });
}

export function useConfirmarRevisaoCondicoes(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: () => confirmarRevisaoCondicoes(baseId),
    onSuccess: () => invalidar(baseId),
  });
}

export function useAprovarBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (justificativa?: string) => aprovarBaseComercial(baseId, justificativa),
    onSuccess: () => invalidar(baseId),
  });
}

export function useRejeitarBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (justificativa: string) => rejeitarBaseComercial(baseId, justificativa),
    onSuccess: () => invalidar(baseId),
  });
}

export function usePublicarBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: () => publicarBaseComercial(baseId),
    onSuccess: () => invalidar(baseId),
  });
}

export function useSuspenderBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (motivo: string) => suspenderBaseComercial(baseId, motivo),
    onSuccess: () => invalidar(baseId),
  });
}

export function useReativarBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: () => reativarBaseComercial(baseId),
    onSuccess: () => invalidar(baseId),
  });
}

export function useSubstituirBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (input: { novaVersaoId: string; motivo?: string }) =>
      substituirBaseComercial(baseId, input.novaVersaoId, input.motivo),
    onSuccess: () => invalidar(baseId),
  });
}

export function useCancelarBaseComercial(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (motivo: string) => cancelarBaseComercial(baseId, motivo),
    onSuccess: () => invalidar(baseId),
  });
}

export function useGerarNovaVersao(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: () => gerarNovaVersao(baseId),
    onSuccess: (nova) => invalidar(nova.id),
  });
}

// -------- Anexos -----------------------------------------------------

export function useEnviarAnexo(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: ({
      file,
      meta,
    }: {
      file: File;
      meta: { categoria?: CategoriaAnexoBaseComercial; descricao?: string };
    }) => enviarAnexo(baseId, file, meta),
    onSuccess: () => invalidar(baseId),
  });
}

export function useRemoverAnexo(baseId: string) {
  const invalidar = useInvalidarBase();
  return useMutation({
    mutationFn: (anexoId: string) => removerAnexo(baseId, anexoId),
    onSuccess: () => invalidar(baseId),
  });
}
